import {AnimatePresence, motion} from 'framer-motion';
import {Box} from 'platform/foundation';

import {ReactNode, useRef} from 'react';

import {useDidUpdate, useForceUpdate} from 'shared/web';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {Portal} from '../../Portal/components/Portal';
import {PortalManager} from '../../Portal/components/PortalManager';
import {NotificationContextProvider} from '../hooks/useNotificationContext';
import {useNotificationsState} from '../hooks/useNotificationsState';
import {Positions} from '../Notification.types';
import {useNotificationsEvents} from '../utils/events';
import {getAnimationStyle} from '../utils/getAnimationStyle';
import {getPositionStyles} from '../utils/getPositionStyles';
import {NotificationContainer} from './NotificationContainer';

export interface NotificationProviderProps {
  /** Notifications position */
  position?: Positions;

  /** Auto close timeout for all notifications, false to disable auto close, can be overwritten for individual notifications by showNotification function */
  autoClose?: number | false;

  /** Maximum amount of notifications displayed at a time, other new notifications will be added to queue */
  limit?: number;

  /** Children to render */
  children: ReactNode;

  /** Print stack trace to console, whenever a notification is shown */
  debug?: boolean;
}

const NOTIFICATION_ITEM_STYLE = {
  paddingTop: 16,
  paddingRight: 16,
} as const;

export function NotificationsProvider(props: NotificationProviderProps) {
  const theme = useComponentsTheme();
  const position = props.position ?? 'top-right';
  const autoClose = props.autoClose ?? 5000;
  const limit = props.limit ?? 4;
  const forceUpdate = useForceUpdate();
  const previousLength = useRef<number>(0);
  const notificationsState = useNotificationsState({limit, debug: props.debug});

  const context = {
    notifications: notificationsState.notifications,
    queue: notificationsState.queue,
    showNotification: notificationsState.showNotification,
    hideNotification: notificationsState.hideNotification,
    updateNotification: notificationsState.updateNotification,
    clean: notificationsState.clean,
    cleanQueue: notificationsState.cleanQueue,
  };

  useNotificationsEvents(context);

  useDidUpdate(() => {
    if (notificationsState.notifications.length > previousLength.current) {
      window.setTimeout(forceUpdate, 0);
    }
    previousLength.current = notificationsState.notifications.length;
  }, [notificationsState.notifications]);

  const items = notificationsState.notifications.map((notification) => (
    <motion.div
      key={notification.id}
      layout
      {...getAnimationStyle(position)}
      style={NOTIFICATION_ITEM_STYLE}
    >
      <NotificationContainer
        notification={notification}
        onHide={notificationsState.hideNotification}
        autoClose={autoClose}
      />
    </motion.div>
  ));

  return (
    <NotificationContextProvider value={context}>
      <PortalManager zIndex={theme.zIndices.TOAST_NOTIFICATION}>
        <Portal>
          <Box
            width={110}
            maxWidth={`calc(100vw - ${NOTIFICATION_ITEM_STYLE.paddingRight}px)`}
            maxHeight={125}
            position="fixed"
            overflowY="auto"
            overflowX="hidden"
            {...getPositionStyles(position)}
          >
            <AnimatePresence initial={false}>{items}</AnimatePresence>
          </Box>
        </Portal>
      </PortalManager>
      {props.children}
    </NotificationContextProvider>
  );
}
